/**
 * Chat store — summarize-job polling.
 *
 * Summarize is a detached server-side job (POST returns 202 + a job snapshot,
 * the renderer polls /status). It survives renderer reloads. Polling only
 * updates the UI; the job runs regardless. When the operator switches away,
 * we drop the timer — the job keeps running, and `syncSummarizeJob` re-attaches
 * when they come back.
 *
 * Polling intervals (SUMMARIZE_POLL_ACTIVE_MS / SUMMARIZE_POLL_RETRY_MS) are
 * defined in uiConstants.ts — the single source for all UI timing constants.
 */
import { apiFetch } from './api';
import type { SummarizeJob } from './chatStore.types.js';
import { getState } from './chatStore.internal.js';
import { addError, addSystemNotice, setSummarizeJob } from './chatStore.mutations.js';
import { SUMMARIZE_POLL_ACTIVE_MS, SUMMARIZE_POLL_RETRY_MS } from './uiConstants';

/**
 * Re-load a conversation from the server after its summary is written — the
 * conversation module's job, passed in by the caller that starts or resumes
 * watching (this module can't import it: conversation loading itself stops
 * and resumes the summarize watch).
 */
export type ReloadConversation = (conversationId: string) => Promise<void>;

let summarizePollTimer: ReturnType<typeof setTimeout> | null = null;

export function stopSummarizePolling(): void {
  if (summarizePollTimer !== null) {
    clearTimeout(summarizePollTimer);
    summarizePollTimer = null;
  }
}

function scheduleSummarizePoll(conversationId: string, delayMs: number, reload: ReloadConversation): void {
  stopSummarizePolling();
  summarizePollTimer = setTimeout(() => void pollSummarizeJob(conversationId, reload), delayMs);
}

async function pollSummarizeJob(conversationId: string, reload: ReloadConversation): Promise<void> {
  // Switched away — stop watching; the job continues server-side and
  // syncSummarizeJob re-attaches if the operator comes back.
  if (getState().conversationId !== conversationId) return;
  try {
    const res = await apiFetch(`/api/chat/sessions/${encodeURIComponent(conversationId)}/summarize/status`);
    if (!res.ok) {
      scheduleSummarizePoll(conversationId, SUMMARIZE_POLL_RETRY_MS, reload);
      return;
    }
    const data = (await res.json()) as { job: SummarizeJob | null };
    if (getState().conversationId !== conversationId) return;
    const job = data.job;
    if (!job) {
      setSummarizeJob(null);
      return;
    }
    if (job.running) {
      setSummarizeJob(job);
      scheduleSummarizePoll(conversationId, SUMMARIZE_POLL_ACTIVE_MS, reload);
      return;
    }
    // Written while a reply is streaming: a reload now would replace the message
    // it is writing into, so look again once the reply is done (a finished job
    // stays readable for ten minutes).
    if (!job.error && !job.cancelled && getState().isStreaming) {
      setSummarizeJob(null);
      scheduleSummarizePoll(conversationId, SUMMARIZE_POLL_RETRY_MS, reload);
      return;
    }
    // Terminal state — build the completion notice BEFORE clearing the job
    // so we can reference its elapsed time.
    const elapsedSec = job.finishedAt
      ? Math.round((new Date(job.finishedAt).getTime() - new Date(job.startedAt).getTime()) / 1000)
      : 0;
    const elapsed =
      elapsedSec >= 60
        ? `${Math.floor(elapsedSec / 60)}:${String(elapsedSec % 60).padStart(2, '0')}`
        : `${elapsedSec}s`;
    setSummarizeJob(null);
    if (job.error) {
      addError(`Summarize failed: ${job.error}`);
    } else if (!job.cancelled) {
      // Checkpoint persisted — re-hydrate from server truth (summary card)
      // and refresh the conversation list.
      await reload(conversationId);
      // Surface what happened — the progress card vanished, the operator
      // needs to know something was actually produced.
      addSystemNotice(`Summary complete (${elapsed}).`);
    }
  } catch {
    // Transient network error — keep polling, the job is server-side.
    scheduleSummarizePoll(conversationId, SUMMARIZE_POLL_RETRY_MS, reload);
  }
}

// ---------------------------------------------------------------------------
// Lifecycle methods — start / cancel / sync. Live here so the timer + the
// commands that touch it stay in one module.
// ---------------------------------------------------------------------------

/** Start a summarize job for the active conversation and watch its progress. */
export async function startSummarize(reload: ReloadConversation): Promise<void> {
  const id = getState().conversationId;
  if (!id) {
    addError('No active conversation to summarize.');
    return;
  }
  if (getState().summarizeJob?.running) return;
  try {
    const res = await apiFetch(`/api/chat/sessions/${encodeURIComponent(id)}/summarize`, {
      method: 'POST',
    });
    const data = (await res.json()) as { job?: SummarizeJob | null; error?: string };
    if (res.status === 409) {
      // 409 can mean (a) already running (attach to it) or (b) turn is
      // active (report why). Handle both: if the response carries a job
      // snapshot, re-attach; otherwise surface the reason so the operator
      // knows what to wait for.
      if (data.job) {
        setSummarizeJob(data.job);
        scheduleSummarizePoll(id, SUMMARIZE_POLL_ACTIVE_MS, reload);
      } else {
        addError(data.error ?? 'Summarize blocked — a turn may be running.');
      }
      return;
    }
    if (!res.ok || !data.job) {
      addError(`Summarize failed: ${data.error ?? res.statusText}`);
      return;
    }
    setSummarizeJob(data.job);
    scheduleSummarizePoll(id, SUMMARIZE_POLL_ACTIVE_MS, reload);
  } catch (e) {
    addError(`Summarize failed: ${e instanceof Error ? e.message : 'unknown error'}`);
  }
}

/** Cancel the running summarize job. The poll loop observes the terminal state. */
export function cancelSummarize(): void {
  const s = getState();
  const id = s.summarizeJob?.conversationId ?? s.conversationId;
  if (!id) return;
  void apiFetch(`/api/chat/sessions/${encodeURIComponent(id)}/summarize/cancel`, {
    method: 'POST',
  }).catch(() => {});
}

/** Re-attach to an in-flight summarize job after a reload or conversation switch. */
export async function syncSummarizeJob(conversationId: string, reload: ReloadConversation): Promise<void> {
  try {
    const res = await apiFetch(`/api/chat/sessions/${encodeURIComponent(conversationId)}/summarize/status`);
    if (!res.ok) return;
    const data = (await res.json()) as { job: SummarizeJob | null };
    if (getState().conversationId !== conversationId) return;
    if (data.job?.running) {
      setSummarizeJob(data.job);
      scheduleSummarizePoll(conversationId, SUMMARIZE_POLL_ACTIVE_MS, reload);
    }
  } catch {
    // Server not running — silently ignore
  }
}
